// Inter (Stride + Nexus body font) is self-hosted via @font-face in globals.css
// — the full upstream file, so the single-story 'a' (cv11) feature survives.
// Monospace — identifiers, codes, slugs (font-mono)
import '@fontsource-variable/geist-mono/index.css'
import './app/globals.css'

import { registerSW } from 'virtual:pwa-register'
import { RouterProvider } from '@tanstack/react-router'
import { createRoot } from 'react-dom/client'
import { Providers } from './components/shared/providers'
import { dismissBootSplash } from './lib/boot-splash'
import { recordEntryBundleExecuted } from './lib/constrained-connection'
import { setAnalyticsManualOptOut } from './lib/gtm'
import { initPostHog } from './lib/posthog'
import { shouldReloadAfterPreloadError } from './lib/preload-recovery'
import { isNative } from './lib/runtime'
import { router } from './router'

recordEntryBundleExecuted()

// Manual GA4 opt-out, checked before GTM/PostHog/anything else initializes.
// Sticky: a later non-excluded sign-in won't undo it.
if (new URLSearchParams(window.location.search).get('stopal_notrack') === '1') {
  setAnalyticsManualOptOut()
}

void initPostHog()

// Capacitor native builds run the same web bundle but talk to the API
// directly and use native push — the browser service worker must not register
// (it would shadow native networking). See lib/runtime.ts.
function registerServiceWorker(): void {
  const register = () =>
    registerSW({
      onRegisterError: (err) => {
        // Surface SW registration failures to BetterStack via the global error
        // hook rather than dropping them silently. The app still works without
        // a service worker (no offline, no precache) — just degraded.
        console.error('sw: registration failed', err)
      },
    })
  if ('requestIdleCallback' in window)
    window.requestIdleCallback(register, { timeout: 2_000 })
  else setTimeout(register, 50)
}

if (!isNative()) {
  if (document.readyState === 'complete') registerServiceWorker()
  else window.addEventListener('load', registerServiceWorker, { once: true })
}

// The website is never offered as an install — phones are sent to the store app
// instead (`InstallPrompt`). The manifest is served on every page, so without
// this Chrome on Android shows its own "Add to Home screen" bar wherever it
// decides the site is installable, which need not be inside the app.
window.addEventListener('beforeinstallprompt', (event) =>
  event.preventDefault(),
)

window.addEventListener('vite:preloadError', (event) => {
  if (!shouldReloadAfterPreloadError()) return
  event.preventDefault()
  window.location.reload()
})

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('Root element #root not found')

createRoot(rootEl).render(
  <Providers>
    <RouterProvider router={router} />
  </Providers>,
)

// Fade the boot splash once the app has painted its first content and the intro
// has been on screen long enough (see lib/boot-splash.ts). "App ready" is the
// first frame after mount — the router/Suspense boundaries render their own
// fallbacks behind the overlay, so we don't wait on data here.
const appReady = new Promise<void>((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
})
dismissBootSplash(appReady)
